/**
 * Stage Plot Editor — Right properties sidebar.
 */
import type { StageElement, PaletteCategory } from '../../shared/types/stageplot';
import { PALETTE_CATEGORIES } from '../../shared/types/stageplot';
import { useTranslation } from 'react-i18next';

export interface PropertiesSidebarProps {
  selectedElement: StageElement | null;
  selectedIds: string[];
  onUpdateElement: (id: string, patch: Partial<StageElement>) => void;
  onUpdateElements: (ids: string[], patch: Partial<StageElement>) => void;
  onDeleteElement: (id: string) => void;
  onDuplicateElement: (id: string) => void;
  onReorderElement: (id: string, place: 'front' | 'back' | 'forward' | 'backward') => void;
  onEditRiser?: (id: string) => void;
}

const SWATCHES = ['#4A90D9', '#50B86C', '#9B59B6', '#E67E22', '#E74C3C', '#1ABC9C', '#E91E63', '#F1C40F', '#7F8C8D', '#2C3E50', '#000000'];

export default function PropertiesSidebar({
  selectedElement,
  selectedIds,
  onUpdateElement,
  onUpdateElements,
  onDeleteElement,
  onDuplicateElement,
  onReorderElement,
  onEditRiser,
}: PropertiesSidebarProps) {
  const { t } = useTranslation();
  if (!selectedElement) {
    return (
      <div className="stageplot-properties">
        <div className="stageplot-properties-empty">
          <p>{t('stagePlotEditor.propsEmpty')}</p>
        </div>
      </div>
    );
  }

  const el = selectedElement;
  // When multiple elements are selected, group changes (colour, flip) apply to all.
  const groupIds = selectedIds.length > 1 ? selectedIds : [el.id];
  const apply = (patch: Partial<StageElement>) =>
    groupIds.length > 1 && selectedIds.length > 1
      ? onUpdateElements(selectedIds, patch)
      : onUpdateElement(el.id, patch);

  return (
    <div className="stageplot-properties">
      <div className="stageplot-properties-header">
        <h3>{PALETTE_CATEGORIES[el.category as PaletteCategory] ?? el.category}</h3>
        <div className="stageplot-properties-header-actions">
          {el.iconId === 'risers' && onEditRiser && (
            <button
              className="btn-icon"
              onClick={() => onEditRiser(el.id)}
              title={t('stagePlotEditor.editRisers')}
              aria-label={t('stagePlotEditor.editRisers')}
            >
              🪜
            </button>
          )}
          <button
            className="btn-icon"
            onClick={() => onDuplicateElement(el.id)}
            title={t('stagePlotEditor.duplicateElementTitle')}
            aria-label={t('stagePlotEditor.duplicateElement')}
          >
            ⧉
          </button>
          <button
            className="btn-icon btn-danger-icon"
            onClick={() => onDeleteElement(el.id)}
            title={t('stagePlotEditor.deleteElement')}
            aria-label={t('stagePlotEditor.deleteElement')}
          >
            🗑
          </button>
        </div>
      </div>

      <div className="stageplot-property-section">
        <h4>{t('stagePlotEditor.layering')}</h4>
        <div className="layering-actions">
          <button
            className="btn-icon"
            onClick={() => onReorderElement(el.id, 'front')}
            title={t('stagePlotEditor.bringToFront')}
            aria-label={t('stagePlotEditor.bringToFront')}
          >
            ⤒
          </button>
          <button
            className="btn-icon"
            onClick={() => onReorderElement(el.id, 'forward')}
            title={t('stagePlotEditor.bringForward')}
            aria-label={t('stagePlotEditor.bringForward')}
          >
            ↑
          </button>
          <button
            className="btn-icon"
            onClick={() => onReorderElement(el.id, 'backward')}
            title={t('stagePlotEditor.sendBackward')}
            aria-label={t('stagePlotEditor.sendBackward')}
          >
            ↓
          </button>
          <button
            className="btn-icon"
            onClick={() => onReorderElement(el.id, 'back')}
            title={t('stagePlotEditor.sendToBack')}
            aria-label={t('stagePlotEditor.sendToBack')}
          >
            ⤓
          </button>
        </div>
      </div>

      <div className="stageplot-property-section">
        <h4>{t('stagePlotEditor.instrumentName')}</h4>
        <div className="form-field">
          <input
            type="text"
            value={el.label}
            aria-label={t('stagePlotEditor.instrumentName')}
            placeholder={t('stagePlotEditor.instrumentName')}
            onChange={(e) => onUpdateElement(el.id, { label: e.target.value })}
          />
        </div>
        <label className="stageplot-show-label" style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, cursor: 'pointer', fontSize: '0.85rem' }}>
          <input
            type="checkbox"
            checked={el.showLabel !== false}
            onChange={(e) => onUpdateElement(el.id, { showLabel: e.target.checked })}
          />
          {t('stagePlotEditor.showTextOnImage')}
        </label>
      </div>

      <div className="stageplot-property-section">
        <h4>{t('stagePlotEditor.colour')}</h4>
        <div className="swatch-row">
          {SWATCHES.map((s) => (
            <button
              key={s}
              className={`swatch ${el.color === s ? 'active' : ''}`}
              style={{ background: s }}
              onClick={() => apply({ color: s })}
              title={s}
              aria-label={t('stagePlotEditor.colourValue', { value: s })}
            />
          ))}
        </div>
        <input
          type="color"
          value={el.color}
          onChange={(e) => apply({ color: e.target.value })}
          className="color-input"
          aria-label={t('stagePlotEditor.customColour')}
        />
      </div>

      <div className="stageplot-property-section">
        <h4>{t('stagePlotEditor.positionSize')}</h4>
        <div className="stageplot-property-row">
          <div className="form-field">
            <label>{t('stagePlotEditor.xM')}</label>
            <input
              type="number"
              step={0.1}
              value={Math.round(el.x * 100) / 100}
              onChange={(e) => onUpdateElement(el.id, { x: Math.max(0, Number(e.target.value) || 0) })}
            />
          </div>
          <div className="form-field">
            <label>{t('stagePlotEditor.yM')}</label>
            <input
              type="number"
              step={0.1}
              value={Math.round(el.y * 100) / 100}
              onChange={(e) => onUpdateElement(el.id, { y: Math.max(0, Number(e.target.value) || 0) })}
            />
          </div>
        </div>
        <div className="stageplot-property-row">
          <div className="form-field">
            <label>{t('stagePlotEditor.widthM')}</label>
            <input
              type="number"
              min={0.1}
              step={0.1}
              value={Math.round(el.width * 100) / 100}
              onChange={(e) => onUpdateElement(el.id, { width: Math.max(0.1, Number(e.target.value) || 0.1) })}
            />
          </div>
          <div className="form-field">
            <label>{t('stagePlotEditor.heightM')}</label>
            <input
              type="number"
              min={0.1}
              step={0.1}
              value={Math.round(el.height * 100) / 100}
              onChange={(e) => onUpdateElement(el.id, { height: Math.max(0.1, Number(e.target.value) || 0.1) })}
            />
          </div>
        </div>
        <div className="stageplot-property-row">
          <div className="form-field">
            <label>{t('stagePlotEditor.rotationDeg')}</label>
            <input
              type="number"
              min={-180}
              max={180}
              step={5}
              value={el.rotation}
              onChange={(e) => apply({ rotation: Math.max(-180, Math.min(180, Number(e.target.value) || 0)) })}
            />
          </div>
          <div className="form-field" style={{ justifyContent: 'flex-end' }}>
            <button
              type="button"
              className={`btn btn-small ${el.flipX ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => apply({ flipX: !el.flipX })}
              title={t('stagePlotEditor.mirrorTitle')}
            >
              {t('stagePlotEditor.reverse')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
